import { lazy, Suspense, type JSX } from "react";
import { Toaster } from "sonner";
import { TgProvider } from "@/shared/telegram/provider";
import { QueryProvider } from "@/app/providers/query-provider";
import { Splash } from "@/widgets/splash";
import { VisitPing } from "@/widgets/visit-ping";
import { ResponsiveNav } from "@/widgets/responsive-nav";
import { RouteSkeleton } from "@/widgets/route-skeleton";
import { ErrorBoundary } from "@/app/providers/error-boundary";
import { Route, Routes } from "react-router";
import "@/app/hydrate";
import "@/app/realtime";

const HomePage = lazy(() =>
  import("@/pages/home").then((m) => ({ default: m.HomePage })),
);
const ProductPage = lazy(() =>
  import("@/pages/product").then((m) => ({ default: m.ProductPage })),
);
const FavoritesPage = lazy(() =>
  import("@/pages/favorites").then((m) => ({ default: m.FavoritesPage })),
);
const CartPage = lazy(() =>
  import("@/pages/cart").then((m) => ({ default: m.CartPage })),
);
const ProfilePage = lazy(() =>
  import("@/pages/profile").then((m) => ({ default: m.ProfilePage })),
);
const OrderPage = lazy(() =>
  import("@/pages/order/[id]").then((m) => ({ default: m.OrderPage })),
);
const ContactsPage = lazy(() =>
  import("@/pages/contacts").then((m) => ({ default: m.ContactsPage })),
);
const CheckoutPage = lazy(() =>
  import("@/pages/checkout").then((m) => ({ default: m.CheckoutPage })),
);
const OrdersPage = lazy(() =>
  import("@/pages/orders").then((m) => ({ default: m.OrdersPage })),
);
const AddressesPage = lazy(() =>
  import("@/pages/addresses").then((m) => ({ default: m.AddressesPage })),
);
const CourierPage = lazy(() =>
  import("@/pages/courier").then((m) => ({ default: m.CourierPage })),
);
const AdminPage = lazy(() =>
  import("@/pages/admin").then((m) => ({ default: m.AdminPage })),
);
const AdminOrdersPage = lazy(() =>
  import("@/pages/admin/orders").then((m) => ({ default: m.AdminOrdersPage })),
);
const AdminReturnsPage = lazy(() =>
  import("@/pages/admin/returns").then((m) => ({ default: m.AdminReturnsPage })),
);
const AdminOrderFulfillPage = lazy(() =>
  import("@/pages/admin/order-fulfill").then((m) => ({ default: m.AdminOrderFulfillPage })),
);
const AdminProductPhotosPage = lazy(() =>
  import("@/pages/admin/product-photos").then((m) => ({ default: m.AdminProductPhotosPage })),
);
const AdminBannersPage = lazy(() =>
  import("@/pages/admin/banners").then((m) => ({ default: m.AdminBannersPage })),
);
const AdminPickupPage = lazy(() =>
  import("@/pages/admin/pickup").then((m) => ({ default: m.AdminPickupPage })),
);
const AdminCategoriesPage = lazy(() =>
  import("@/pages/admin/categories").then((m) => ({ default: m.AdminCategoriesPage })),
);
const AdminDictionaryPage = lazy(() =>
  import("@/pages/admin/dictionary").then((m) => ({ default: m.AdminDictionaryPage })),
);
const AdminProductsPage = lazy(() =>
  import("@/pages/admin/products").then((m) => ({ default: m.AdminProductsPage })),
);
const AdminSettingsPage = lazy(() =>
  import("@/pages/admin/settings").then((m) => ({ default: m.AdminSettingsPage })),
);
const AdminUsersPage = lazy(() =>
  import("@/pages/admin/users").then((m) => ({ default: m.AdminUsersPage })),
);
const AdminAnalyticsPage = lazy(() =>
  import("@/pages/admin/analytics").then((m) => ({ default: m.AdminAnalyticsPage })),
);
const AdminPromotionsPage = lazy(() =>
  import("@/pages/admin/promotions").then((m) => ({ default: m.AdminPromotionsPage })),
);
const AdminCampaignsPage = lazy(() =>
  import("@/pages/admin/campaigns").then((m) => ({ default: m.AdminCampaignsPage })),
);

function Shell() {
  return (
    <TgProvider>
      <Splash />
      <VisitPing />
      <div className="app-shell">
        <Suspense fallback={<RouteSkeleton />}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/contacts" element={<ContactsPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/addresses" element={<AddressesPage />} />
            <Route path="/courier" element={<CourierPage />} />
            <Route path="/order/:id" element={<OrderPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/returns" element={<AdminReturnsPage />} />
            <Route path="/admin/order/:id" element={<AdminOrderFulfillPage />} />
            <Route path="/admin/product/:id/photos" element={<AdminProductPhotosPage />} />
            <Route path="/admin/promotions" element={<AdminPromotionsPage />} />
            <Route path="/admin/campaigns" element={<AdminCampaignsPage />} />
            <Route path="/admin/banners" element={<AdminBannersPage />} />
            <Route path="/admin/pickup" element={<AdminPickupPage />} />
            <Route path="/admin/categories" element={<AdminCategoriesPage />} />
            <Route path="/admin/dictionary" element={<AdminDictionaryPage />} />
            <Route path="/admin/products" element={<AdminProductsPage />} />
            <Route path="/admin/settings" element={<AdminSettingsPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
          </Routes>
          </ErrorBoundary>
        </Suspense>
        <ResponsiveNav />
      </div>
      <Toaster position="top-center" richColors closeButton />
    </TgProvider>
  );
}

export function App(): JSX.Element {
  return (
    <QueryProvider>
      <Shell />
    </QueryProvider>
  );
}
